import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export interface DualRoleRequest {
  id: string;
  user_id: string;
  requested_role: "mentor" | "entrepreneur";
  status: "pending" | "approved" | "declined";
  reason: string | null;
  requested_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  updated_at: string;
}

export function useMyDualRoleRequest() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["dual_role_request", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("dual_role_requests")
        .select("*")
        .eq("user_id", user.id)
        .order("requested_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as DualRoleRequest | null) ?? null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRequestDualRole() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (requestedRole: "mentor" | "entrepreneur") => {
      if (!user?.id) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("dual_role_requests")
        .insert({
          user_id: user.id,
          requested_role: requestedRole,
          status: "pending",
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dual_role_request"] });
      toast.success("Role request submitted for review");
    },
    onError: (error: Error) => {
      toast.error("Failed to submit request: " + error.message);
    },
  });
}
